import { defineStore } from 'pinia'

export const supportedLocales = ['en', 'de', 'fr'] as const
export type Locale = (typeof supportedLocales)[number]

/** BCP 47 tags used by Intl, radix-vue calendars, and @internationalized/date formatters. */
export const intlLocaleMap: Record<Locale, string> = {
  de: 'de-DE',
  en: 'en-GB',
  fr: 'fr-FR'
}

/**
 * Map an app locale code to a BCP 47 tag for date/calendar formatting.
 *
 * :param locale: App locale (`de`, `en`, `fr`)
 * :return: BCP 47 locale string (e.g. `de-DE`)
 */
export const toIntlLocale = (locale: Locale): string => intlLocaleMap[locale] ?? 'de-DE'

const detectInitialLocale = (): Locale => {
  // Use browser language as the boot-time default. After fetchUser() resolves,
  // applyEffectiveLanguage() overrides this with the backend-resolved chain
  // (preferred_language → org.legal_jurisdiction → 'de').
  const navigatorLocale = (navigator.language || navigator.languages[0]) as string
  console.debug('[localeStore] detectInitialLocale — navigator.language:', navigatorLocale)

  if (supportedLocales.includes(navigatorLocale as Locale)) {
    console.debug('[localeStore] using navigator.language:', navigatorLocale)
    return navigatorLocale as Locale
  }

  const baseLocale = navigatorLocale.split('-')[0]
  if (supportedLocales.includes(baseLocale as Locale)) {
    console.debug('[localeStore] using navigator base locale:', baseLocale)
    return baseLocale as Locale
  }

  console.debug('[localeStore] falling back to de')
  return 'de' // Fallback to German to match the backend default (LANGUAGE_CODE)
}

export const useLocaleStore = defineStore('locale', {
  state: () => ({
    locale: detectInitialLocale()
  }),

  actions: {
    setLocale(newLocale: Locale) {
      console.debug('[localeStore] setLocale:', this.locale, '→', newLocale)
      this.locale = newLocale
    }
  },

  getters: {
    getLocale(state): Locale {
      return state.locale
    }
  }
})

/**
 * Apply the backend-resolved effective interface language to the locale store.
 *
 * The backend computes `effective_language` using the chain:
 *   user preferred_language → organisation legal_jurisdiction (Rechtsordnung) → 'de'.
 * The organisation's `default_language` ("Phonebot Sprache") is intentionally
 * not part of this resolution.
 *
 * This is shared between the customer (`userStore`) and admin (`admin/user.ts`)
 * stores so both honour the backend chain identically.
 *
 * Must be called within a Pinia-active context (it instantiates `useLocaleStore`).
 */
export const applyEffectiveLanguage = (effectiveLanguage: string | null | undefined): void => {
  if (!effectiveLanguage) return
  if (!supportedLocales.includes(effectiveLanguage as Locale)) return

  console.debug('[localeStore] applyEffectiveLanguage — effective_language:', effectiveLanguage)
  const localeStore = useLocaleStore()
  localeStore.setLocale(effectiveLanguage as Locale)
}
